import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { v4 as uuidv4 } from 'uuid';
import type {
  Project,
  OverlayElement,
  VideoMetadata,
  ExportSettings,
  HistoryState,
  Tool,
  PlayerState,
  TimelineState,
  SelectionState,
  DragState,
  RenderProgress,
  TrimRange,
} from '@/types';

// Maximum history entries for undo/redo
const MAX_HISTORY = 50;

interface ProjectStore {
  // Project state
  project: Project;
  
  // UI state
  selectedTool: Tool;
  playerState: PlayerState;
  timelineState: TimelineState;
  selectionState: SelectionState;
  dragState: DragState;
  renderProgress: RenderProgress;
  
  // History for undo/redo
  history: HistoryState[];
  historyIndex: number;
  
  // Modals and panels
  isExportPanelOpen: boolean;
  isProjectSettingsOpen: boolean;
  
  // Actions - Project
  newProject: () => void;
  loadProject: (project: Project) => void;
  saveProject: () => Project;
  setProjectName: (name: string) => void;
  
  // Actions - Video
  setVideo: (video: VideoMetadata, blob?: Blob) => void;
  setVideoBlob: (blob: Blob | null) => void;
  clearVideo: () => void;
  
  // Actions - Trim
  setTrimRange: (trimRange: TrimRange) => void;
  setTrimStart: (time: number) => void;
  setTrimEnd: (time: number) => void;
  toggleTrimEnabled: () => void;
  resetTrim: () => void;
  
  // Actions - Overlays
  addOverlay: (overlay: OverlayElement) => void;
  updateOverlay: (id: string, updates: Partial<OverlayElement>) => void;
  deleteOverlay: (id: string) => void;
  deleteSelectedOverlays: () => void;
  duplicateOverlay: (id: string) => void;
  reorderOverlay: (id: string, direction: 'up' | 'down' | 'top' | 'bottom') => void;
  
  // Actions - Selection
  selectOverlay: (id: string, addToSelection?: boolean) => void;
  deselectAll: () => void;
  selectAll: () => void;
  setHoveredOverlay: (id: string | null) => void;
  
  // Actions - Tools
  setSelectedTool: (tool: Tool) => void;
  
  // Actions - Player
  setIsPlaying: (isPlaying: boolean) => void;
  setCurrentTime: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setPlaybackRate: (rate: number) => void;
  
  // Actions - Timeline
  setTimelineZoom: (zoom: number) => void;
  setTimelineScroll: (position: number) => void;
  toggleSnap: () => void;
  
  // Actions - Drag
  startDrag: (dragState: Partial<DragState>) => void;
  updateDrag: (currentPoint: { x: number; y: number }) => void;
  endDrag: () => void;
  
  // Actions - Export
  setExportSettings: (settings: Partial<ExportSettings>) => void;
  setRenderProgress: (progress: Partial<RenderProgress>) => void;
  openExportPanel: () => void;
  closeExportPanel: () => void;
  
  // Actions - History
  undo: () => void;
  redo: () => void;
  pushHistory: (description: string) => void;
  
  // Helpers
  getOverlaysAtTime: (time: number) => OverlayElement[];
  getSelectedOverlays: () => OverlayElement[];
}

// Initial states
const initialPlayerState: PlayerState = {
  isPlaying: false,
  currentTime: 0,
  volume: 1,
  isMuted: false,
  playbackRate: 1,
};

const initialTimelineState: TimelineState = {
  zoom: 1,
  scrollPosition: 0,
  snapEnabled: true,
  trackHeight: 36,
};

const initialSelectionState: SelectionState = {
  selectedIds: [],
  hoveredId: null,
};

const initialDragState: DragState = {
  isDragging: false,
  dragType: null,
  startPoint: null,
  currentPoint: null,
  resizeHandle: null,
};

const initialRenderProgress: RenderProgress = {
  isRendering: false,
  progress: 0,
  estimatedTimeRemaining: 0,
  currentPhase: 'preparing',
};

const createInitialProject = (): Project => ({
  id: uuidv4(),
  name: 'Untitled Project',
  version: '1.0.0',
  schemaVersion: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  video: null,
  videoBlob: null,
  trimRange: null,
  overlays: [],
  exportSettings: {
    resolution: '1080p',
    quality: 'high',
    format: 'mp4',
    preserveAudio: true,
  },
});

export const useProjectStore = create<ProjectStore>()(
  immer((set, get) => ({
    // Initial state
    project: createInitialProject(),
    selectedTool: 'select',
    playerState: initialPlayerState,
    timelineState: initialTimelineState,
    selectionState: initialSelectionState,
    dragState: initialDragState,
    renderProgress: initialRenderProgress,
    history: [],
    historyIndex: -1,
    isExportPanelOpen: false,
    isProjectSettingsOpen: false,
    
    // Project actions
    newProject: () => {
      set((state) => {
        state.project = createInitialProject();
        state.selectionState = initialSelectionState;
        state.playerState = initialPlayerState;
        state.history = [];
        state.historyIndex = -1;
      });
    },
    
    loadProject: (project) => {
      set((state) => {
        state.project = project;
        state.selectionState = initialSelectionState;
        state.playerState = { ...initialPlayerState };
        state.history = [];
        state.historyIndex = -1;
      });
    },
    
    saveProject: () => {
      const state = get();
      return {
        ...state.project,
        updatedAt: new Date().toISOString(),
      };
    },
    
    setProjectName: (name) => {
      set((state) => {
        state.project.name = name;
        state.project.updatedAt = new Date().toISOString();
      });
    },
    
    // Video actions
    setVideo: (video, blob) => {
      set((state) => {
        state.project.video = video;
        if (blob) {
          state.project.videoBlob = blob;
        }
        // Initialize trim range with full video duration (disabled by default)
        state.project.trimRange = {
          startTime: 0,
          endTime: video.duration,
          enabled: false,
        };
        state.project.updatedAt = new Date().toISOString();
        state.playerState.currentTime = 0;
      });
      get().pushHistory('Import video');
    },
    
    setVideoBlob: (blob) => {
      set((state) => {
        state.project.videoBlob = blob;
      });
    },
    
    clearVideo: () => {
      set((state) => {
        state.project.video = null;
        state.project.videoBlob = null;
        state.project.trimRange = null;
        state.project.overlays = [];
        state.project.updatedAt = new Date().toISOString();
        state.selectionState = initialSelectionState;
      });
    },
    
    // Trim actions
    setTrimRange: (trimRange) => {
      set((state) => {
        state.project.trimRange = trimRange;
        state.project.updatedAt = new Date().toISOString();
      });
    },
    
    setTrimStart: (time) => {
      set((state) => {
        const video = state.project.video;
        if (!video) return;
        
        if (!state.project.trimRange) {
          state.project.trimRange = {
            startTime: time,
            endTime: video.duration,
            enabled: true,
          };
        } else {
          state.project.trimRange.startTime = Math.max(0, Math.min(time, state.project.trimRange.endTime - 0.1));
          state.project.trimRange.enabled = true;
        }
        state.project.updatedAt = new Date().toISOString();
      });
    },
    
    setTrimEnd: (time) => {
      set((state) => {
        const video = state.project.video;
        if (!video) return;
        
        if (!state.project.trimRange) {
          state.project.trimRange = {
            startTime: 0,
            endTime: time,
            enabled: true,
          };
        } else {
          state.project.trimRange.endTime = Math.min(video.duration, Math.max(time, state.project.trimRange.startTime + 0.1));
          state.project.trimRange.enabled = true;
        }
        state.project.updatedAt = new Date().toISOString();
      });
    },
    
    toggleTrimEnabled: () => {
      set((state) => {
        if (state.project.trimRange) {
          state.project.trimRange.enabled = !state.project.trimRange.enabled;
          state.project.updatedAt = new Date().toISOString();
        }
      });
    },
    
    resetTrim: () => {
      set((state) => {
        const video = state.project.video;
        if (video) {
          state.project.trimRange = {
            startTime: 0,
            endTime: video.duration,
            enabled: false,
          };
        } else {
          state.project.trimRange = null;
        }
        state.project.updatedAt = new Date().toISOString();
      });
    },
    
    // Overlay actions
    addOverlay: (overlay) => {
      set((state) => {
        state.project.overlays.push(overlay);
        state.project.updatedAt = new Date().toISOString();
        state.selectionState.selectedIds = [overlay.id];
      });
      get().pushHistory(`Add ${overlay.type}`);
    },
    
    updateOverlay: (id, updates) => {
      set((state) => {
        const index = state.project.overlays.findIndex((o) => o.id === id);
        if (index !== -1) {
          state.project.overlays[index] = {
            ...state.project.overlays[index],
            ...updates,
          } as OverlayElement;
          state.project.updatedAt = new Date().toISOString();
        }
      });
    },
    
    deleteOverlay: (id) => {
      set((state) => {
        state.project.overlays = state.project.overlays.filter((o) => o.id !== id);
        state.selectionState.selectedIds = state.selectionState.selectedIds.filter(
          (selectedId) => selectedId !== id
        );
        state.project.updatedAt = new Date().toISOString();
      });
      get().pushHistory('Delete overlay');
    },
    
    deleteSelectedOverlays: () => {
      const { selectedIds } = get().selectionState;
      if (selectedIds.length === 0) return;
      
      set((state) => {
        state.project.overlays = state.project.overlays.filter(
          (o) => !selectedIds.includes(o.id)
        );
        state.selectionState.selectedIds = [];
        state.project.updatedAt = new Date().toISOString();
      });
      get().pushHistory(`Delete ${selectedIds.length} overlay(s)`);
    },
    
    duplicateOverlay: (id) => {
      const overlay = get().project.overlays.find((o) => o.id === id);
      if (!overlay) return;
      
      const newOverlay: OverlayElement = {
        ...JSON.parse(JSON.stringify(overlay)),
        id: uuidv4(),
        name: `${overlay.name} (copy)`,
        bounds: {
          ...overlay.bounds,
          x: overlay.bounds.x + 20,
          y: overlay.bounds.y + 20,
        },
      };
      
      set((state) => {
        state.project.overlays.push(newOverlay);
        state.selectionState.selectedIds = [newOverlay.id];
        state.project.updatedAt = new Date().toISOString();
      });
      get().pushHistory('Duplicate overlay');
    },
    
    reorderOverlay: (id, direction) => {
      set((state) => {
        const overlays = state.project.overlays;
        const currentIndex = overlays.findIndex((o) => o.id === id);
        if (currentIndex === -1) return;
        
        const overlay = overlays[currentIndex];
        const currentZIndex = overlay.zIndex;
        
        switch (direction) {
          case 'up': {
            // Find the next overlay with higher zIndex
            const nextOverlay = overlays.find((o) => o.zIndex === currentZIndex + 1);
            if (nextOverlay) {
              nextOverlay.zIndex = currentZIndex;
              overlay.zIndex = currentZIndex + 1;
            }
            break;
          }
          case 'down': {
            // Find the overlay with lower zIndex
            const prevOverlay = overlays.find((o) => o.zIndex === currentZIndex - 1);
            if (prevOverlay) {
              prevOverlay.zIndex = currentZIndex;
              overlay.zIndex = currentZIndex - 1;
            }
            break;
          }
          case 'top': {
            // Move to top (highest zIndex)
            const maxZIndex = Math.max(...overlays.map((o) => o.zIndex));
            if (currentZIndex < maxZIndex) {
              // Shift all overlays above current down by 1
              overlays.forEach((o) => {
                if (o.zIndex > currentZIndex) {
                  o.zIndex -= 1;
                }
              });
              overlay.zIndex = maxZIndex;
            }
            break;
          }
          case 'bottom': {
            // Move to bottom (lowest zIndex = 0)
            if (currentZIndex > 0) {
              // Shift all overlays below current up by 1
              overlays.forEach((o) => {
                if (o.zIndex < currentZIndex) {
                  o.zIndex += 1;
                }
              });
              overlay.zIndex = 0;
            }
            break;
          }
        }
        
        state.project.updatedAt = new Date().toISOString();
      });
      get().pushHistory('Reorder overlay');
    },
    
    // Selection actions
    selectOverlay: (id, addToSelection = false) => {
      set((state) => {
        if (addToSelection) {
          if (state.selectionState.selectedIds.includes(id)) {
            state.selectionState.selectedIds = state.selectionState.selectedIds.filter(
              (selectedId) => selectedId !== id
            );
          } else {
            state.selectionState.selectedIds.push(id);
          }
        } else {
          state.selectionState.selectedIds = [id];
        }
      });
    },
    
    deselectAll: () => {
      set((state) => {
        state.selectionState.selectedIds = [];
      });
    },
    
    selectAll: () => {
      set((state) => {
        state.selectionState.selectedIds = state.project.overlays.map((o) => o.id);
      });
    },
    
    setHoveredOverlay: (id) => {
      set((state) => {
        state.selectionState.hoveredId = id;
      });
    },
    
    // Tool actions
    setSelectedTool: (tool) => {
      set((state) => {
        state.selectedTool = tool;
      });
    },
    
    // Player actions
    setIsPlaying: (isPlaying) => {
      set((state) => {
        state.playerState.isPlaying = isPlaying;
      });
    },
    
    setCurrentTime: (time) => {
      set((state) => {
        state.playerState.currentTime = time;
      });
    },
    
    setVolume: (volume) => {
      set((state) => {
        state.playerState.volume = Math.max(0, Math.min(1, volume));
      });
    },
    
    toggleMute: () => {
      set((state) => {
        state.playerState.isMuted = !state.playerState.isMuted;
      });
    },
    
    setPlaybackRate: (rate) => {
      set((state) => {
        state.playerState.playbackRate = rate;
      });
    },
    
    // Timeline actions
    setTimelineZoom: (zoom) => {
      set((state) => {
        state.timelineState.zoom = Math.max(0.1, Math.min(10, zoom));
      });
    },
    
    setTimelineScroll: (position) => {
      set((state) => {
        state.timelineState.scrollPosition = Math.max(0, position);
      });
    },
    
    toggleSnap: () => {
      set((state) => {
        state.timelineState.snapEnabled = !state.timelineState.snapEnabled;
      });
    },
    
    // Drag actions
    startDrag: (dragState) => {
      set((state) => {
        state.dragState = {
          ...state.dragState,
          ...dragState,
          isDragging: true,
        };
      });
    },
    
    updateDrag: (currentPoint) => {
      set((state) => {
        state.dragState.currentPoint = currentPoint;
      });
    },
    
    endDrag: () => {
      set((state) => {
        state.dragState = initialDragState;
      });
    },
    
    // Export actions
    setExportSettings: (settings) => {
      set((state) => {
        state.project.exportSettings = {
          ...state.project.exportSettings,
          ...settings,
        };
        state.project.updatedAt = new Date().toISOString();
      });
    },
    
    setRenderProgress: (progress) => {
      set((state) => {
        state.renderProgress = {
          ...state.renderProgress,
          ...progress,
        };
      });
    },
    
    openExportPanel: () => {
      set((state) => {
        state.isExportPanelOpen = true;
      });
    },
    
    closeExportPanel: () => {
      set((state) => {
        state.isExportPanelOpen = false;
      });
    },
    
    // History actions
    undo: () => {
      const { history, historyIndex } = get();
      if (historyIndex <= 0) return;
      
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      
      set((state) => {
        state.project.overlays = JSON.parse(JSON.stringify(previousState.overlays));
        state.historyIndex = newIndex;
        state.project.updatedAt = new Date().toISOString();
      });
    },
    
    redo: () => {
      const { history, historyIndex } = get();
      if (historyIndex >= history.length - 1) return;
      
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      
      set((state) => {
        state.project.overlays = JSON.parse(JSON.stringify(nextState.overlays));
        state.historyIndex = newIndex;
        state.project.updatedAt = new Date().toISOString();
      });
    },
    
    pushHistory: (description) => {
      set((state) => {
        // Remove any redo history
        state.history = state.history.slice(0, state.historyIndex + 1);
        
        // Add new state
        state.history.push({
          overlays: JSON.parse(JSON.stringify(state.project.overlays)),
          timestamp: Date.now(),
          description,
        });
        
        // Limit history size
        if (state.history.length > MAX_HISTORY) {
          state.history = state.history.slice(-MAX_HISTORY);
        }
        
        state.historyIndex = state.history.length - 1;
      });
    },
    
    // Helper methods
    getOverlaysAtTime: (time) => {
      return get().project.overlays.filter(
        (overlay) => overlay.startTime <= time && overlay.endTime >= time && overlay.visible
      );
    },
    
    getSelectedOverlays: () => {
      const { selectedIds } = get().selectionState;
      return get().project.overlays.filter((o) => selectedIds.includes(o.id));
    },
  }))
);

// Selectors for optimized re-renders
export const selectProject = (state: ProjectStore) => state.project;
export const selectVideo = (state: ProjectStore) => state.project.video;
export const selectOverlays = (state: ProjectStore) => state.project.overlays;
export const selectSelectedTool = (state: ProjectStore) => state.selectedTool;
export const selectPlayerState = (state: ProjectStore) => state.playerState;
export const selectTimelineState = (state: ProjectStore) => state.timelineState;
export const selectSelectionState = (state: ProjectStore) => state.selectionState;
export const selectDragState = (state: ProjectStore) => state.dragState;
export const selectRenderProgress = (state: ProjectStore) => state.renderProgress;
export const selectCanUndo = (state: ProjectStore) => state.historyIndex > 0;
export const selectCanRedo = (state: ProjectStore) => state.historyIndex < state.history.length - 1;

